/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */
import * as MsDyn365 from '@msdyn365-commerce/core';
import { ArrayExtensions } from '@msdyn365-commerce-modules/retail-actions';
import { getTelemetryObject, IAlertProps, IModuleProps, INodeProps, ITelemetryContent } from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import * as React from 'react';

import { CookieComplianceAcceptButton, CookieComplianceLinks } from './components';
import { IPhibroCCookieComplianceConfig, IPhibroCCookieComplianceProps } from './phibro-c-cookie-compliance.props.autogenerated';

/**
 * Local storage key used to remember cookie rejection.
 */
const COOKIE_REJECTED_STORAGE_KEY = 'phibro_cookie_rejected';

/**
 * Alert state interface.
 */
export interface IAlertState {
    visible: boolean;
}

/**
 * Cookie compliance view props interface.
 */
export interface ICookieComplianceViewProps extends IPhibroCCookieComplianceProps<{}> {
    CookieComplianceBanner: IModuleProps;
    AlertProps: IAlertProps;
    Content: INodeProps;
    text?: React.ReactNode;
    links?: React.ReactNode;
    acceptButton?: React.ReactNode;
    className: string;
    onDismiss(): void;
}

/**
 *
 * CookieCompliance component.
 * @extends {React.PureComponent<IPhibroCCookieComplianceProps<IHeroConfig>>}
 */
class CookieCompliance extends React.PureComponent<IPhibroCCookieComplianceProps<IPhibroCCookieComplianceConfig>, IAlertState> {
    private readonly cookieRef: React.RefObject<HTMLInputElement>;

    private readonly telemetryContent: ITelemetryContent = getTelemetryObject(
        this.props.context.request.telemetryPageName!,
        this.props.friendlyName,
        this.props.telemetry
    );

    public constructor(props: IPhibroCCookieComplianceProps<IPhibroCCookieComplianceConfig>) {
        super(props);
        this.state = { visible: false };
        this.cookieRef = React.createRef();
        this._onDismiss = this._onDismiss.bind(this);
        this._onReject = this._onReject.bind(this);
    }

    public componentDidMount(): void {
        const { context } = this.props;

        if (
            context &&
            context.request &&
            context.request.cookies &&
            !context.request.cookies.isConsentGiven() &&
            !this._isCookiesRejected()
        ) {
            this.setState({ visible: true });
        }
    }

    public render(): JSX.Element | null {
        const { config, context, resources, telemetry } = this.props;
        const { acceptCookiesButtonText, acceptCookiesAriaLabel } = resources;
        const { visible } = this.state;

        const isConsentGiven = context && context.request && context.request.cookies && context.request.cookies.isConsentGiven();

        const isCookiesRejected = this._isCookiesRejected();

        let moduleConfig: IPhibroCCookieComplianceConfig;

        try {
            moduleConfig = config;
        } catch (error) {
            telemetry.error(`Something went wrong while rendering the alert module ------${error}`);
            return <div>{error}</div>;
        }

        /**
         * Do not show the cookie banner if:
         * - User has already accepted cookies, OR
         * - User has already rejected cookies.
         */
        if ((isConsentGiven || isCookiesRejected) && !visible) {
            return null;
        }

        if (!MsDyn365.msdyn365Commerce.isBrowser) {
            return <></>;
        }

        const viewProps = {
            ...this.props,
            onDismiss: this._onDismiss,
            CookieComplianceBanner: {
                moduleProps: this.props,
                className: classnames('ms-cookie-compliance', moduleConfig.className),
                ref: this.cookieRef
            },
            AlertProps: {
                className: 'ms-cookie-compliance__container',
                color: 'white',
                fade: false
            },
            Content: {
                className: 'ms-cookie-compliance__content'
            },
            text: moduleConfig.content && (
                <MsDyn365.RichTextComponent
                    className='ms-cookie-compliance__text'
                    text={moduleConfig.content}
                    editProps={{ onEdit: this.handleContentChange, requestContext: this.props.context.request }}
                />
            ),
            links: ArrayExtensions.hasElements(moduleConfig.actionLinks) && (
                <CookieComplianceLinks
                    links={moduleConfig.actionLinks}
                    onTextChange={this.handleLinkTextChange}
                    requestContext={this.props.context.request}
                    telemetryContent={this.telemetryContent}
                />
            ),
            acceptButton: (
                <>
                    <CookieComplianceAcceptButton
                        onClose={this._onDismiss}
                        acceptCookiesButtonText={acceptCookiesButtonText}
                        acceptCookiesButtonAriaLabel={acceptCookiesAriaLabel}
                        telemetryContent={this.telemetryContent}
                    />

                    <button
                        type='button'
                        className='ms-cookie-compliance__reject-button'
                        onClick={this._onReject}
                        aria-label='Reject cookies'
                    >
                        Reject
                    </button>
                </>
            )
        };

        return this.props.renderView(viewProps) as React.ReactElement;
    }

    /**
     * Handles Link content change event.
     * @param linkIndex - Index of link in linkarray.
     * @returns Void.
     */
    public handleLinkTextChange = (linkIndex: number) => (event: MsDyn365.ContentEditableEvent): void => {
        const actionLinks = this.props.config.actionLinks;

        if (!Array.isArray(actionLinks)) {
            return;
        }

        if (!Number.isInteger(linkIndex) || linkIndex < 0) {
            return;
        }

        let currentIndex = 0;

        const link = actionLinks.find(() => {
            const matches = currentIndex === linkIndex;
            currentIndex += 1;
            return matches;
        });

        if (!link) {
            return;
        }

        link.linkText = String(event.target.value);
    };

    /**
     * Handles content change event.
     * @param event - Event.
     */
    public handleContentChange = (event: MsDyn365.ContentEditableEvent): void => {
        this.props.config.content = event.target.value;
    };

    /**
     * Handles Accept button.
     */
    private _onDismiss(): void {
        const { context } = this.props;

        /**
         * Remove the rejection flag if the user accepts cookies.
         */
        this._clearCookiesRejected();

        if (context && context.request && context.request.cookies) {
            context.request.cookies.setConsentCookie();
            location.reload();
        }

        this.setState({ visible: false });
    }

    /**
     * Handles Reject button.
     *
     * We intentionally do not call setConsentCookie()
     * because the user has not given consent.
     */
    private _onReject(): void {
        const { context } = this.props;

        if (MsDyn365.msdyn365Commerce.isBrowser) {
            try {
                localStorage.setItem(COOKIE_REJECTED_STORAGE_KEY, 'true');
            } catch (error) {
                this.props.telemetry.error(`Unable to store cookie rejection state: ${error}`);
            }
        }

        if (context && context.request && context.request.cookies) {
            context.request.cookies.deleteConsentCookie();
        }

        this.setState({ visible: false });
    }

    /**
     * Checks whether the user has previously rejected cookies.
     */
    private _isCookiesRejected(): boolean {
        if (!MsDyn365.msdyn365Commerce.isBrowser) {
            return false;
        }

        try {
            return localStorage.getItem(COOKIE_REJECTED_STORAGE_KEY) === 'true';
        } catch (error) {
            return false;
        }
    }

    /**
     * Clears the cookie rejection flag.
     */
    private _clearCookiesRejected(): void {
        if (!MsDyn365.msdyn365Commerce.isBrowser) {
            return;
        }

        try {
            localStorage.removeItem(COOKIE_REJECTED_STORAGE_KEY);
        } catch (error) {
            this.props.telemetry.error(`Unable to clear cookie rejection state: ${error}`);
        }
    }
}

export default CookieCompliance;
